import firebaseConfig from '../../../firebase-applet-config.json';

/**
 * Browser- and Node-safe Firebase Emulator Suite configuration.
 *
 * Keep this module data-only: it is imported by the Vite client and by the
 * Node seed scripts, so it must not read `import.meta.env`, `process.env`, or
 * initialize a Firebase SDK. This is the single source of truth for the local
 * emulator identity and endpoints.
 */
export const LOCAL_EMULATOR_COMPANY = {
  id: 'comp_rich_test',
  name: 'Rich Test Co',
} as const;

export const LOCAL_EMULATOR_USER = {
  uid: 'rich-test-user',
  email: 'test@datacrate.io',
  password: 'test-password-123',
  displayName: 'Rich Test User',
  claims: {
    role: 'owner',
    companyId: LOCAL_EMULATOR_COMPANY.id,
    companyRole: 'admin',
  },
} as const;

export const ISOLATED_SYNTHETIC_EMULATOR_COMPANY = {
  id: 'comp_synthetic_audit',
  name: 'Synthetic Audit Co',
} as const;

export const ISOLATED_SYNTHETIC_EMULATOR_USER = {
  uid: 'synthetic-audit-user',
  email: 'synthetic-audit@datacrate.io',
  password: 'synthetic-password-123',
  displayName: 'Synthetic Audit User',
  claims: {
    role: 'owner',
    companyId: ISOLATED_SYNTHETIC_EMULATOR_COMPANY.id,
    companyRole: 'admin',
  },
} as const;

export const ISOLATED_SYNTHETIC_EMULATOR_PORTS = {
  auth: 19099,
  firestore: 18080,
  storage: 19199,
} as const;

/** Loopback endpoint shared by the browser client and local seed commands. */
export const LOCAL_EMULATOR_HOST = '127.0.0.1';

/** Must match the `emulators` block in firebase.json. */
export const LOCAL_EMULATOR_PORTS = {
  auth: 9099,
  firestore: 8080,
  storage: 9199,
} as const;

export interface LocalEmulatorPorts {
  readonly auth: number;
  readonly firestore: number;
  readonly storage: number;
}

/**
 * Parse a dedicated all-loopback emulator stack for hermetic browser gates.
 * Normal local development remains pinned to LOCAL_EMULATOR_PORTS; this helper
 * exists so a fresh test stack never has to kill or seed a developer's stack.
 */
export function resolveAlternateLocalEmulatorPorts(values: {
  readonly auth?: string;
  readonly firestore?: string;
  readonly storage?: string;
}): LocalEmulatorPorts {
  const parsePort = (value: string | undefined, field: string): number => {
    if (!value || !/^[0-9]{4,5}$/u.test(value)) {
      throw new Error(`${field} must be a decimal loopback port from 1024 to 65535.`);
    }
    const parsed = Number(value);
    if (!Number.isSafeInteger(parsed) || parsed < 1024 || parsed > 65_535) {
      throw new Error(`${field} must be a decimal loopback port from 1024 to 65535.`);
    }
    return parsed;
  };
  const ports = {
    auth: parsePort(values.auth, 'Auth emulator port'),
    firestore: parsePort(values.firestore, 'Firestore emulator port'),
    storage: parsePort(values.storage, 'Storage emulator port'),
  };
  if (new Set(Object.values(ports)).size !== 3) {
    throw new Error('Auth, Firestore, and Storage emulator ports must be distinct.');
  }
  return Object.freeze(ports);
}

/**
 * The seed scripts must target the same project, named database, and bucket as
 * the browser client. The checked-in Firebase client config remains canonical.
 */
export const LOCAL_EMULATOR_FIREBASE = {
  projectId: firebaseConfig.projectId,
  firestoreDatabaseId: firebaseConfig.firestoreDatabaseId,
  storageBucket: firebaseConfig.storageBucket,
} as const;

export const REQUIRED_EMULATOR_HOST_ENV_KEYS = [
  'FIREBASE_AUTH_EMULATOR_HOST',
  'FIRESTORE_EMULATOR_HOST',
  'FIREBASE_STORAGE_EMULATOR_HOST',
] as const;

export type RequiredEmulatorHostEnvKey = typeof REQUIRED_EMULATOR_HOST_ENV_KEYS[number];

/** Exact loopback endpoints accepted by destructive local seed operations. */
export const LOCAL_EMULATOR_HOST_ENV = {
  FIREBASE_AUTH_EMULATOR_HOST: `${LOCAL_EMULATOR_HOST}:${LOCAL_EMULATOR_PORTS.auth}`,
  FIRESTORE_EMULATOR_HOST: `${LOCAL_EMULATOR_HOST}:${LOCAL_EMULATOR_PORTS.firestore}`,
  FIREBASE_STORAGE_EMULATOR_HOST: `${LOCAL_EMULATOR_HOST}:${LOCAL_EMULATOR_PORTS.storage}`,
} as const satisfies Record<RequiredEmulatorHostEnvKey, string>;

/** Return every missing emulator host guard without consulting global state. */
export function missingEmulatorHostEnvKeys(
  env: Readonly<Record<string, string | undefined>>,
): RequiredEmulatorHostEnvKey[] {
  return REQUIRED_EMULATOR_HOST_ENV_KEYS.filter((key) => !env[key]?.trim());
}

/**
 * Return every host that is missing or does not match the reviewed loopback
 * endpoint. Presence alone is not a sufficient guard for recursive seed
 * cleanup because Firebase Admin accepts an arbitrary emulator hostname.
 */
export function invalidEmulatorHostEnvKeys(
  env: Readonly<Record<string, string | undefined>>,
): RequiredEmulatorHostEnvKey[] {
  return REQUIRED_EMULATOR_HOST_ENV_KEYS.filter(
    (key) => env[key]?.trim() !== LOCAL_EMULATOR_HOST_ENV[key],
  );
}
